import React, {useRef, useState} from "react";
import {Button, Col, notification, Popconfirm, Popover, Radio, Row, Table, Tooltip, Typography} from "antd";
import moment from "moment/moment";
import {PREFERRED_DATETIME_FORMAT} from "../../util/time";
import _ from "lodash";
import {DownloadOutlined, OrderedListOutlined, RedoOutlined} from "@ant-design/icons";
import {Message} from "./Message";
import {
  downloadD365BackupSummary,
  downloadD365Export,
  resubmitD365Export,
  updateD365ExportLoadStatus
} from "../../api/d365Api";
import {downloadCsvFile, downloadExcelFile, downloadTxtFile} from "../../util/download";
import sorters from "../../util/sorters";
import {useDynamicTableHeight} from "../../util/useDynamicTableHeight";
import {getPaginationConfig} from "../../util/showAllPagination";

const ExportsTable = ({
                        exports,
                        loading,
                        setLoading,
                        permissions,
                        hideColumns,
                        editMap,
                        setEditMap,
                        pendingProjectCodes,
                        notificationError,
                        loadExports,
                        disableExports,
                        setSelectedExport,
                        setShowHourLog
                      }) => {
  const [resubmitStatus, setResubmitStatus] = useState(false);
  const [loadStatusUpdating, setLoadStatusUpdating] = useState(false);
  const [selectedLoadStatus, setSelectedLoadStatus] = useState('NONE');
  const [downloadStatus, setDownloadStatus] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);

  const loadStatusOptions = [
    {label: 'Generated', value: 'GENERATED'},
    {label: 'Submitted', value: 'SUBMITTED'},
    {label: 'Loaded', value: 'LOADED'},
    {label: 'Canceled', value: 'CANCELED'},
  ];
  const exportIsDone = exp => exp.exportStatus === 'COMPLETED' || exp.exportStatus === 'FAILED';
  const exportHasLogs = exp => exp.exportFlags && exp.exportFlags.includes("HAS_LOG")
  const hasPendingProjects = (exp) => {
    if (_.isEmpty(exp.projectCodes)) {
      return pendingProjectCodes.length === 0
    } else {
      const codes = exp.projectCodes.split(",")
      for (let i = 0; i < codes.length; i++)
        if (pendingProjectCodes.includes(codes[i])) {
          return true;
        }
    }
    return false;
  }

  const editExport = (exp, edit) => {
    const newEditMap = _.cloneDeep(editMap);
    _.each(Object.keys(newEditMap), k => {
      newEditMap[k] = false
    });
    newEditMap[exp.ID] = edit;
    setEditMap(newEditMap);
    setSelectedLoadStatus(exp.loadStatus);
    setShowConfirm(false);
  };

  const updateExport = async (exp) => {
    try {
      setLoadStatusUpdating(true);
      setShowConfirm(false);
      await updateD365ExportLoadStatus(exp.ID, selectedLoadStatus);
      notification.success({
        message: 'Success',
        description: `Load status for export ${exp.filename} has been updated!`,
        placement: 'bottomRight',
      });
      loadExports();
    } catch (e) {
      console.error(`Could not update load status for export ${exp.filename}`);
    } finally {
      setLoadStatusUpdating(false);
      editExport(exp, false);
    }
  };


  const resubmitExport = async (exp) => {
    let res;
    try {
      setLoading(true)
      setResubmitStatus(true)
      res = await resubmitD365Export(exp.ID);
      if (res) {
        if (res.error) {
          notificationError(res.error)
        } else if (res.status && res.status === "ok") {
          notification.success({
            message: 'Success',
            description: `${_.capitalize(exp.exportType)} export has been started!`,
            placement: 'bottomRight',
          });
          loadExports();
        } else {
          console.error(res)
          notificationError('Unknown error - see console');
        }
      } else {
        notificationError('No data from resubmitD365Export');
      }
    } catch (e) {
      console.error(`Could not resubmit export ${exp.filename}`, e);
      notificationError('Unknown error - see console')
    } finally {
      setResubmitStatus(false)
      setLoading(false)
    }
  }

  const checkConfirm = (exp) => {
    if (exp.loadStatus !== selectedLoadStatus && ['LOADED', 'CANCELED'].includes(selectedLoadStatus)) {
      setShowConfirm(true);
    } else {
      updateExport(exp);
    }
  };


  const loadHourLog = exp => {
    setSelectedExport(exp);
    setShowHourLog(true);
  };


  const downloadExport = async (exp) => {
    let res
    try {
      setDownloadStatus(true);
      res = await downloadD365Export(exp.ID);
      if (res) {
        if (res.data) {
          if (_.endsWith(exp.filename, '.xlsx')) {
            downloadExcelFile(res.data, exp.filename);
          } else if (_.endsWith(exp.filename, '.csv')) {
            downloadCsvFile(res.data, exp.filename);
          } else {
            notification.warning({
              message: 'Unsupported file type',
              description: `Export has unsupported file type: ${exp.filename}`,
              placement: 'bottomRight',
            });
          }
        } else if (res.error) {
          notificationError(res.error);
        } else {
          console.error(res)
          notificationError('Unknown error - see console');
        }
      } else {
        notificationError('No data from downloadD365Export')
      }
    } catch (e) {
      console.error(`Could not download export ${exp.filename}`, e);
      notificationError('Unknown error - see console');
    } finally {
      setDownloadStatus(false);
    }
  };

  const downloadTxtSummary = async (exp) => {
    let res
    try {
      res = await downloadD365BackupSummary(exp.ID);
      if (res) {
        if (res.data) {
          if (_.endsWith(exp.backupFilename, '.txt')) {
            downloadTxtFile(res.data, exp.backupFilename);
          } else {
            notification.warning({
              message: 'Unsupported file type',
              description: `Export has unsupported file type: ${exp.filename}`,
              placement: 'bottomRight',
            });
          }
        } else if (res.error) {
          notificationError(res.error);
        } else {
          console.error(res)
          notificationError('Unknown error - see console');
        }
      } else {
        notificationError('No data from downloadD365BackupSummary')
      }
    } catch (e) {
      console.error(`Could not download backup summary ${exp.filename}`, e);
      notificationError('Unknown error - see console');
    }
  };


  const columns = [
    {
      title: 'Date Created',
      width: 165,
      sorter: (a, b) => sorters.date(a.CreatedAt, b.CreatedAt),
      render: r => (
        <Tooltip title={`Created by ${r.createdByEmail}`} placement="bottom">
          <Typography.Text>{moment(r.CreatedAt).format(PREFERRED_DATETIME_FORMAT)}</Typography.Text>
        </Tooltip>
      )
    },
    {
      title: 'Export Type',
      width: 115,
      sorter: (a, b) => sorters.date(a.exportType, b.exportType),
      render: r => (
        <Popover
          placement="right"
          overlayStyle={{
            width: "70vw"
          }}
          trigger="hover,click,focus"
          content={(
            <>
              <Row>
                <Col span={3}><b>Export ID</b></Col>
                <Col span={21}>{r.ID}</Col>
              </Row>
              {r.exportType === 'HOURS' && (
                <Row>
                  <Col span={3}><b>Date Filter</b></Col>
                  <Col span={21}>{r.dateFilter}</Col>
                </Row>
              )}
              <Row>
                <Col span={3}><b>Project Code(s)</b></Col>
                <Col span={21}>{r.projectCodes}</Col>
              </Row>
              {r.exportType === 'HOURS' && (
                <Row>
                  <Col span={3}><b>Worker ID(s)</b></Col>
                  <Col span={21}>{r.workerIds}</Col>
                </Row>
              )}
            </>
          )}
        >
          <Button>{_.capitalize(r.exportType)}</Button>
        </Popover>
      ),
    },
    {
      title: 'Date Filter',
      width: 180,
      sorter: (a, b) => sorters.string(a.dateFilter, b.dateFilter),
      dataIndex: 'dateFilter',
      hidden: hideColumns
    },
    {
      title: 'Project Codes',
      width: 500,
      ellipsis: true,
      dataIndex: 'projectCodes',
      hidden: hideColumns
    },
    {
      title: 'Worker Ids',
      width: 200,
      ellipsis: true,
      dataIndex: 'workerIds',
      hidden: hideColumns
    },
    {
      title: 'Export Status',
      width: 125,
      dataIndex: 'exportStatus',
      sorter: (a, b) => sorters.string(a.exportStatus, b.exportStatus),
      render: r => _.capitalize(r)
    },
    {
      title: 'Load Status',
      width: 110,
      sorter: (a, b) => sorters.string(a.loadStatus, b.loadStatus),
      render: r => (
        permissions !== null && permissions.canManageD365 && (r.loadStatus === 'GENERATED' || r.loadStatus === 'SUBMITTED') ? (
          <Popover
            content={(
              <>
                <Row style={{marginBottom: 10}}>
                  <Radio.Group
                    options={loadStatusOptions}
                    onChange={e => setSelectedLoadStatus(e.target.value)}
                    value={selectedLoadStatus}
                    optionType="button"
                    size="small"
                    buttonStyle="solid"/>
                </Row>
                <Row>
                  <Col span={1}>
                  </Col>
                  <Col span={23}>
                    <Popconfirm
                      title="Please confirm this change, as this action cannot be undone."
                      onConfirm={() => updateExport(r)}
                      onCancel={() => editExport(r, false)}
                      okText="Confirm"
                      cancelText="Cancel"
                      open={showConfirm}
                    >
                      <Button
                        type="primary"
                        size="small"
                        style={{float: 'right'}}
                        loading={loadStatusUpdating}
                        onClick={() => checkConfirm(r)}
                      >
                        Save
                      </Button>
                    </Popconfirm>
                    <Button
                      size="small"
                      style={{float: 'right', marginRight: 10}}
                      onClick={() => editExport(r, false)}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </>
            )}
            title="Change Load Status"
            trigger="none"
            open={editMap[r.ID] === true}
          >
            <Tooltip title={r.updatedByEmail && r.updatedByEmail !== '' ? `Changed by ${r.updatedByEmail}` : ''}
                     align="bottom" placement="bottom">
              <Button type="link" style={{padding: 0}}
                      onClick={() => editExport(r, true)}>{_.capitalize(r.loadStatus)}</Button>
            </Tooltip>
          </Popover>
        ) : _.capitalize(r.loadStatus)
      )
    },
    {
      title: 'Filename',
      width: 280,
      sorter: (a, b) => sorters.string(a.filename, b.filename),
      dataIndex: 'filename'
    },
    {
      title: 'Successes',
      width: 90,
      align: 'right',
      render: r => (
        <Popover
          placement="top"
          overlayStyle={{
            width: "15vw"
          }}
          trigger="click,focus"
          content={(
            <>
              {r.exportType === 'HOURS' && (
                <Row>
                  <Col><b>{r.exportStatus === 'COMPLETED' ? r.recordCountSuccess : '-'} hours exported</b></Col>
                  <Col><Button icon={<DownloadOutlined/>}
                               style={{marginRight: 10}}
                               disabled={!(exportIsDone(r) && (r.recordCountSuccess + r.recordCountWarning) > 0)}
                               loading={downloadStatus}
                               onClick={() => downloadTxtSummary(r)}>Download Backup Summary</Button></Col>
                </Row>
              )}
              {r.exportType === 'RATES' && (
                <Row>
                  <Col><b>{r.exportStatus === 'COMPLETED' ? r.recordCountSuccess : '-'} rates exported</b></Col>
                </Row>
              )}
            </>
          )}
        >
          <Button>{exportIsDone(r) ? r.recordCountSuccess : '-'}</Button>
        </Popover>
      ),
    },
    {
      title: 'Warnings',
      width: 80,
      align: 'right',
      render: r => exportIsDone(r) ? r.recordCountWarning : '-'
    },
    {
      title: 'Errors',
      width: 80,
      align: 'right',
      render: r => exportIsDone(r) ? r.recordCountError : '-'
    },
    {
      title: 'Message',
      ellipsis: true,
      width: 300,
      render: r => (<Message message={r.message}/>)
    },
    {
      title: 'Action',
      width: 260,
      key: 'action',
      fixed: 'right',
      hidden: permissions === null,
      render: (text, record) => (
        <>
          {permissions !== null && permissions.canViewD365 && (
            <>
              {record.exportType === 'HOURS' && record.exportStatus === 'FAILED' &&
                (
                  <Popconfirm
                    title="Are you sure to resubmit this export?"
                    onConfirm={() => resubmitExport(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Popover
                      placement="topRight"
                      trigger="hover"
                      content={(
                        <Typography>Only available when there are no pending exports, and Rates are all fully
                          loaded</Typography>
                      )}
                    >
                      <Button icon={<RedoOutlined/>}
                              style={{marginRight: 5}}
                              disabled={disableExports || hasPendingProjects(record) ||
                                (record.exportFlags ? record.exportFlags.includes("NO_RESUBMIT") : false)}
                              loading={resubmitStatus}>Resubmit</Button>
                    </Popover>
                  </Popconfirm>
                )}
              {(record.exportType === 'RATES' || (record.exportType === 'HOURS' && record.exportStatus !== 'FAILED')) &&
                (<Button icon={<DownloadOutlined/>}
                         style={{marginRight: 5}}
                         onClick={() => downloadExport(record)}
                         disabled={record.exportStatus !== 'COMPLETED' ||
                           !(record.exportFlags && record.exportFlags.includes("HAS_FILE"))}
                         loading={downloadStatus}>Download</Button>
                )}
              {record.exportType === 'HOURS' && (
                <Button icon={<OrderedListOutlined/>}
                        disabled={!exportIsDone(record) || !exportHasLogs(record)}
                        onClick={() => loadHourLog(record)}>View Log</Button>
              )}
            </>
          )}
        </>
      ),
    },
  ].filter(item => !item.hidden);

  return (
    <>
      <Table
        ref={tableContainerRef}
        rowKey="filename"
        dataSource={exports}
        columns={columns}
        scroll={{x: 1500, y: tableHeight}}
        pagination={getPaginationConfig(exports.length)}
        size="small"
        loading={loading}
      />
    </>
  );
}

export {ExportsTable};